/**
 * PublicDemoScreener — landing-page illustration of the premium-selling
 * screener (Wheel & Other Strategies engine), served from precomputed
 * static JSON frozen as of 2025-12-31. No auth, no DB, no live inference.
 * DTE toggle: 14 / 35 / 45. Standard risk tier only.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { C } from "@/components/PublicShell";

const API = process.env.REACT_APP_BACKEND_URL;
let _cache = null;
const fetchScreener = () => {
  if (!_cache) _cache = fetch(`${API}/api/demo/screener`).then(r => { if (!r.ok) throw new Error(r.status); return r.json(); });
  return _cache;
};

const STRAT_COLORS = { CSP: "#34d399", BPS: "#60a5fa", BCS: "#f87171" };
const REGIME_SHORT = { "Quiet Range": "Quiet", "Bullish Trend": "Bull", "Bearish Trend": "Bear", "Volatile": "Vol", Quiet: "Quiet", Bull: "Bull", Bear: "Bear", Vol: "Vol" };
const REGIME_COLORS = { Quiet: "#9ca3af", Bull: "#34d399", Bear: "#f87171", Vol: "#fbbf24" };

export const PublicDemoScreener = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [dte, setDte] = useState("35");

  useEffect(() => { fetchScreener().then(setData).catch(() => setErr(true)); }, []);

  if (err) return null;
  const rows = data?.dte?.[dte] || [];

  return (
    <div className="border rounded-lg overflow-hidden" style={{ borderColor: C.border, background: C.card }} data-testid="demo-screener">
      <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div>
          <div className="text-sm font-bold" style={{ color: C.text }}>Premium-Selling Screener — demo</div>
          <div className="text-[10px] mt-0.5" style={{ color: C.muted }}>
            Frozen snapshot as of {data?.as_of || "2025-12-31"} · {data?.band || "Standard risk tier"}
          </div>
        </div>
        <div className="flex items-center gap-1" data-testid="demo-screener-dte-toggle">
          {["14", "35", "45"].map(d => (
            <button key={d} onClick={() => setDte(d)}
              className="h-7 px-3 text-[11px] font-semibold rounded-md transition-colors"
              style={dte === d
                ? { background: C.accent, color: "#0a0a0a" }
                : { border: `1px solid ${C.border}`, color: C.muted }}
              data-testid={`demo-screener-dte-${d}`}>
              {d} DTE
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left" data-testid="demo-screener-table">
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Ticker", "Sector", "Price", "~IVR (proxy)", "Regime", "Strategy", "Strikes", "Premium", "RoR", "Y%/wk", "Δ", "POP"].map(h => (
                <th key={h} className="px-3 py-2 text-[10px] font-medium whitespace-nowrap"
                  style={{ color: C.muted, fontFamily: "'JetBrains Mono', monospace" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!data && (
              <tr><td colSpan={12} className="px-3 py-6 text-center text-xs" style={{ color: C.muted }}>Loading demo data…</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.ticker} style={{ borderBottom: `1px solid ${C.border}` }} data-testid={`demo-screener-row-${r.ticker}`}>
                <td className="px-3 py-2 text-xs font-bold" style={{ color: C.text }}>{r.ticker}</td>
                <td className="px-3 py-2 text-[10px] whitespace-nowrap" style={{ color: C.muted }}>{r.sector}</td>
                <td className="px-3 py-2 text-[11px] font-mono" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>${r.price}</td>
                <td className="px-3 py-2 text-[11px]" style={{ color: r.iv_rank > 60 ? "#f87171" : r.iv_rank < 35 ? "#fbbf24" : C.text, fontFamily: "'JetBrains Mono', monospace" }}>~{r.iv_rank}</td>
                <td className="px-3 py-2 text-[10px] whitespace-nowrap" style={{ color: REGIME_COLORS[REGIME_SHORT[r.regime]] || C.muted, fontFamily: "'JetBrains Mono', monospace" }}>{REGIME_SHORT[r.regime] || r.regime}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{ color: STRAT_COLORS[r.strategy] || C.text, border: `1px solid ${STRAT_COLORS[r.strategy] || C.border}` }}>
                    {r.strategy}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px] whitespace-nowrap" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{r.strikes}</td>
                <td className="px-3 py-2 text-[11px]" style={{ color: "#34d399", fontFamily: "'JetBrains Mono', monospace" }}>${r.premium}</td>
                <td className="px-3 py-2 text-[11px]" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{r.ror_pct}%</td>
                <td className="px-3 py-2 text-[11px]" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{r.ypw_pct}%</td>
                <td className="px-3 py-2 text-[11px]" style={{ color: C.muted, fontFamily: "'JetBrains Mono', monospace" }}>{r.delta}</td>
                <td className="px-3 py-2 text-[11px]" style={{ color: C.text, fontFamily: "'JetBrains Mono', monospace" }}>{r.pop_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-2.5 flex items-center justify-between flex-wrap gap-2"
        style={{ borderTop: `1px solid ${C.border}`, background: C.cardAlt }}>
        <span className="text-[10px] italic" style={{ color: C.muted }} data-testid="demo-screener-disclaimer">
          Illustration only — historical snapshot with Black-Scholes-estimated premiums, not live quotes.
          Not a recommendation to buy or sell any security.
        </span>
        <button onClick={() => navigate("/sign-up")}
          className="h-8 px-4 text-xs font-semibold rounded-md inline-flex items-center gap-1.5 transition-opacity hover:opacity-85 shrink-0"
          style={{ background: C.accent, color: "#0a0a0a" }}
          data-testid="demo-screener-cta">
          Run it live on your tickers <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};
